import React, { useMemo, useRef, useState, useEffect } from "react";import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { buildTraceable, GUIDES } from "@/data/curriculumGeometry";
import { useCursiveFont } from "@/lib/cursiveFont";
import { analyzeStrokes, TOLERANCE_MODES } from "@/lib/scoring";
import { TracingCanvas } from "@/components/tracing/TracingCanvas";
import { ScoreDisplay } from "@/components/ScoreDisplay";
import { Confetti } from "@/components/Confetti";
import { MusicToggle } from "@/components/MusicToggle";
import { startMusic, stopMusic, playCheer } from "@/lib/sfx";
import {
  Play, Pause, RotateCcw, Undo2, Eraser, Eye, CheckCircle2, ArrowLeft, Save, Sparkles,
} from "lucide-react";

const STAGES = [
  { key: "watch", title: "Watch", label: "Watch" },
  { key: "guided", title: "Trace It", guideOpacity: 0.4, showLetter: true, showStart: true, tolerance: TOLERANCE_MODES.standard, threshold: 70, mode: "trace", instruction: "Trace from the dot and follow the line." },
  { key: "faded", title: "Practice It Yourself", guideOpacity: 0.15, showLetter: true, showStart: true, tolerance: TOLERANCE_MODES.standard, threshold: 75, mode: "practice", instruction: "The guide is faint — write over it carefully." },
  { key: "independent", title: "Write It Yourself", guideOpacity: 0, showLetter: false, showStart: false, tolerance: TOLERANCE_MODES.standard + 2, threshold: 80, mode: "independent", instruction: "No guide now — write it from memory on the lines!" },
];

function blankSheet(width = 200) {
  return {
    viewBox: `0 0 ${width} 100`,
    width,
    strokes: [{ d: `M 6 ${GUIDES.base} L ${width - 6} ${GUIDES.base}` }],
    startingPoint: { x: 8, y: GUIDES.base },
    guidelines: GUIDES,
    get fullPath() { return this.strokes.map((x) => x.d).join(" "); },
  };
}

function findActivity(overlay, id) {
  if (!overlay) return null;
  for (const lvl of overlay.levels)
    for (const g of lvl.groups)
      for (const a of g.activities)
        if (a.id === id) return { ...a, level_number: lvl.level_number, level_title: lvl.title, level_id: lvl.id };
  return null;
}

// The next unlocked activity in curriculum order after `currentId`.
function nextActivityId(overlay, currentId) {
  if (!overlay) return null;
  const flat = [];
  for (const lvl of overlay.levels)
    for (const g of lvl.groups)
      for (const a of g.activities) flat.push(a);
  const idx = flat.findIndex((a) => a.id === currentId);
  if (idx === -1) return null;
  for (let i = idx + 1; i < flat.length; i++) {
    if (flat[i].status && flat[i].status !== "locked") return flat[i].id;
  }
  return null;
}

function Ctrl({ onClick, disabled, testId, children, variant = "default" }) {
  const styles = {
    default: "bg-white border-2 border-[#E5DECF] text-[#1F2937] hover:border-[#0D9488] hover:bg-[#F2ECE1]",
    primary: "bg-[#0D9488] text-white shadow-md hover:bg-[#0B7F74] hover:shadow-lg",
    accent: "bg-[#F59E0B] text-white shadow-md hover:bg-[#D97706] hover:shadow-lg",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-base font-bold transition-all active:translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed ${styles[variant]}`}>
      {children}
    </button>
  );
}

export default function CurriculumLesson() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const { overlay, refreshCurriculum } = useApp();

  const activity = useMemo(() => findActivity(overlay, activityId), [overlay, activityId]);
  const isIndependent = activity?.type === "independent";
  const isChallenge = activity?.type === "challenge";
  // Kick off the Cedarville Cursive font load — re-render once available so the
  // traceable geometry re-computes with font-derived paths (which line up
  // pixel-perfectly with the visible glyph the child traces).
  const cursiveFont = useCursiveFont();
  const traceable = useMemo(() => {
    if (!activity) return null;
    if (isIndependent) return blankSheet(220);
    if (isChallenge) return blankSheet(160);
    return buildTraceable(activity);
    // cursiveFont is a dependency so the traceable recomputes when the font loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, isIndependent, isChallenge, cursiveFont]);

  const canvasRef = useRef(null);
  const animatorRef = useRef(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [strokeCount, setStrokeCount] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);
  const [showScore, setShowScore] = useState(false);
  const [passed, setPassed] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [checking, setChecking] = useState(false);
  const [lastResp, setLastResp] = useState(null);
  const advanceTimer = useRef(null);

  const cfg = STAGES[stageIdx];

  useEffect(() => { setStageIdx(0); setStrokeCount(0); canvasRef.current?.clear(); }, [activityId]);
  useEffect(() => () => { if (advanceTimer.current) clearTimeout(advanceTimer.current); }, []);

  // Playful ukulele loop while the cursive lesson is on screen; stops on unmount.
  useEffect(() => {
    startMusic();
    return () => stopMusic();
  }, []);

  // Fire a kid-crowd cheer when confetti pops.
  useEffect(() => { if (celebrate) playCheer(); }, [celebrate]);

  if (!activity) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="font-heading text-xl font-bold text-[#6B7280]">Loading activity…</p>
        <button onClick={() => navigate("/")} className="mt-4 rounded-2xl bg-[#0D9488] px-5 py-3 font-bold text-white">Go Home</button>
      </div>
    );
  }
  if (!traceable) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <div className="text-5xl mb-3">🛠️</div>
        <h1 className="font-heading text-2xl font-extrabold text-[#1F2937]">{activity.title}</h1>
        <p className="mt-2 font-body text-[#6B7280]">This activity is being prepared and will be playable soon.</p>
        <button onClick={() => navigate(-1)} className="mt-5 rounded-2xl bg-[#0D9488] px-5 py-3 font-bold text-white">Back</button>
      </div>
    );
  }

  const resetCanvas = () => { canvasRef.current?.clear(); setStrokeCount(0); };

  const postAttempt = async (score, sub, mode) => {
    try {
      const resp = await api.curriculumAttempt({
        activity_id: activity.id, score,
        path_accuracy: sub.pathAccuracy, completion_score: sub.completion,
        starting_position_score: sub.startingPosition, attempt_mode: mode,
      });
      setLastResp(resp);
      await refreshCurriculum();
      return resp;
    } catch (e) {
      toast.error("Couldn't save progress. Please try again.");
      return null;
    }
  };

  // ----- trace-based flow -----
  const handleCheck = async () => {
    const strokes = canvasRef.current?.getStrokes() || [];
    if (strokes.length === 0) return;
    setChecking(true);
    const res = analyzeStrokes(strokes, traceable, { tolerance: cfg.tolerance });
    setResult(res);
    const didPass = res.score >= cfg.threshold;
    setPassed(didPass);
    setShowScore(true);
    await postAttempt(res.score, res, cfg.mode);
    setChecking(false);
  };

  const handleContinue = () => {
    setShowScore(false);
    if (cfg.key === "independent") {
      if (lastResp?.mastered) {
        // Activity completed & mastered — celebrate, then move on to the next
        // unlocked activity automatically.
        const next = nextActivityId(lastResp?.overlay, activity.id);
        setCelebrate(true);
        advanceTimer.current = setTimeout(() => {
          setCelebrate(false);
          if (next) navigate(`/activity/${next}`, { replace: true });
          else navigate(`/level/${activity.level_id}`, { replace: true });
        }, 1600);
      } else {
        resetCanvas();
      }
      return;
    }
    resetCanvas();
    setStageIdx((i) => Math.min(i + 1, STAGES.length - 1));
  };

  const handleRetry = () => { setShowScore(false); resetCanvas(); };

  // ----- independent flow -----
  const saveIndependent = async () => {
    const strokes = canvasRef.current?.getStrokes() || [];
    if (strokes.length === 0) { toast.error("Write something first!"); return; }
    setChecking(true);
    await api.saveSample({ activity_id: activity.id, prompt: activity.content, mode: "prompt", stroke_data: strokes });
    await api.curriculumAttempt({ activity_id: activity.id, score: 100, completion_score: 100, attempt_mode: "independent" });
    await refreshCurriculum();
    setChecking(false);
    toast.success("Your writing was saved! Your teacher can review it.");
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 3500);
    resetCanvas();
  };

  const nextLevelStage = cfg.key === "guided" ? "Practice It Yourself" : cfg.key === "faded" ? "Write It Yourself" : "Next Activity";

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      <button onClick={() => navigate(-1)} data-testid="lesson-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#0D9488]">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Level {activity.level_number} · {activity.level_title}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]" data-testid="lesson-title">{activity.title}</h1>
        </div>
        {!isIndependent && !isChallenge && (
          <div className="flex items-center gap-3" data-testid="stage-stepper-wrap">
            <MusicToggle />
            <div className="flex items-center gap-1.5" data-testid="stage-stepper">
              {STAGES.map((s, i) => (
                <div key={s.key} className={`h-2.5 rounded-full transition-all ${i <= stageIdx ? "w-8 bg-[#0D9488]" : "w-4 bg-[#E5DECF]"}`} title={s.label} />
              ))}
            </div>
          </div>
        )}
        {(isIndependent || isChallenge) && (
          <MusicToggle />
        )}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <div className="rounded-[28px] border border-[#E5DECF] bg-white p-4 sm:p-6">
          <h2 className="mb-3 font-heading text-lg font-bold text-[#1F2937]" data-testid="stage-title">
            {isIndependent ? "Independent Writing" : isChallenge ? "Challenge" : cfg.title}
          </h2>
          <div className="rounded-3xl bg-[#FAF7F2] p-2 ring-1 ring-[#E5DECF]">
            <TracingCanvas
              key={activity.id}
              ref={canvasRef}
              letter={traceable}
              animatorRef={animatorRef}
              drawingEnabled={isIndependent || isChallenge || cfg.key !== "watch"}
              guideOpacity={isIndependent ? 0 : isChallenge ? 0 : cfg.key === "watch" ? 0.5 : cfg.guideOpacity}
              showStart={!isIndependent && !isChallenge && (cfg.key === "watch" ? true : cfg.showStart)}
              showLetterGuide={!isIndependent && !isChallenge && (cfg.key === "watch" ? true : cfg.showLetter)}
              onStrokesChange={(s) => setStrokeCount(s.length)}
            />
          </div>

          {isIndependent ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Ctrl testId="undo-button" disabled={strokeCount === 0} onClick={() => canvasRef.current?.undo()}><Undo2 size={18} /> Undo</Ctrl>
              <Ctrl testId="clear-button" disabled={strokeCount === 0} onClick={resetCanvas}><Eraser size={18} /> Clear</Ctrl>
              <Ctrl testId="save-writing-button" variant="primary" disabled={strokeCount === 0 || checking} onClick={saveIndependent}><Save size={18} /> {checking ? "Saving…" : "Save My Writing"}</Ctrl>
            </div>
          ) : cfg.key === "watch" ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Ctrl testId="anim-play-button" variant="accent" onClick={() => { if (playing) { animatorRef.current?.pause(); setPlaying(false); } else { animatorRef.current?.play(); setPlaying(true); } }}>
                {playing ? <Pause size={18} /> : <Play size={18} />} {playing ? "Pause" : "Play"}
              </Ctrl>
              <Ctrl testId="anim-replay-button" onClick={() => { animatorRef.current?.replay(); setPlaying(true); }}><RotateCcw size={18} /> Replay</Ctrl>
              <Ctrl testId="ready-to-trace-button" variant="primary" onClick={() => { resetCanvas(); animatorRef.current?.reset(); setPlaying(false); setStageIdx(1); }}>I'm Ready to Trace <CheckCircle2 size={18} /></Ctrl>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Ctrl testId="undo-button" disabled={strokeCount === 0} onClick={() => canvasRef.current?.undo()}><Undo2 size={18} /> Undo</Ctrl>
              <Ctrl testId="clear-button" disabled={strokeCount === 0} onClick={resetCanvas}><Eraser size={18} /> Clear</Ctrl>
              <Ctrl testId="show-me-button" onClick={() => animatorRef.current?.replay()}><Eye size={18} /> Show Me</Ctrl>
              <Ctrl testId="try-again-button" onClick={resetCanvas}><RotateCcw size={18} /> Try Again</Ctrl>
              <Ctrl testId="check-my-writing-button" variant="primary" disabled={strokeCount === 0 || checking} onClick={handleCheck}><CheckCircle2 size={18} /> {checking ? "Checking…" : "Check My Writing"}</Ctrl>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-[28px] border border-[#E5DECF] bg-white p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">Example</div>
            <div className="mt-2 flex items-center justify-center rounded-2xl bg-[#F2ECE1] py-5 px-3">
              <span className="font-cursive text-[#0D9488] text-center break-words" style={{ fontSize: activity.content && activity.content.length > 8 ? "2.6rem" : "4.5rem", lineHeight: 1.1 }}>
                {activity.content || activity.title}
              </span>
            </div>
            {activity.content && activity.type !== "prewriting" && (
              <div className="mt-2 text-center font-body text-sm text-[#6B7280]">Printed: <span className="font-semibold text-[#1F2937]">{activity.content}</span></div>
            )}
          </div>

          <div className="rounded-[28px] border border-[#E5DECF] bg-white p-6">
            <div className="flex items-center gap-2 text-[#0D9488]"><Sparkles size={18} /><h3 className="font-heading text-lg font-bold">How to do it</h3></div>
            <p className="mt-2 font-body text-[#1F2937]">
              {isIndependent ? activity.content || "Write freely on the lines, then save your work." :
               cfg.key === "watch" ? "Press Play to watch the pencil form the shape, then get ready to trace." : cfg.instruction}
            </p>
            {!isIndependent && cfg.key !== "watch" && (
              <div className="mt-3 text-sm text-[#6B7280]">Reach <span className="font-bold text-[#0D9488]">{cfg.threshold}%</span> to move on. Mastery at <span className="font-bold text-[#0D9488]">{activity.mastery_score}%</span>.</div>
            )}
          </div>
        </div>
      </div>

      <ScoreDisplay open={showScore} result={result} passed={passed} mastered={passed && cfg.key === "independent"} onRetry={handleRetry} onContinue={handleContinue} continueLabel={nextLevelStage} />
      {celebrate && <Confetti />}
      {celebrate && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="fixed inset-x-0 bottom-8 z-50 mx-auto w-fit rounded-full bg-[#10B981] px-6 py-3 text-white font-bold shadow-xl" data-testid="mastered-banner">
          🌟 Nice work on {activity.title}!
        </motion.div>
      )}
    </div>
  );
}
